import { useEffect, useState, useRef } from "react";
import { Button } from "../components/ui/button";
import {
  Loader2,
  Info,
  Mail,
  ShoppingBag,
  Lock,
  ArrowRight,
  Store,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Alert, AlertDescription } from "../components/ui/alert";

import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { shopifyBackendBaseUrl } from "../admin/dashboards/urls";
import { analytics } from "../analytics";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { updateMiqCollection } from "wasp/client/operations";
import { useTranslation, TranslationKey } from "../i18n/hooks";

type ShopifyConnectionViewProps = {
  storeName: string;
  shop: string | null;
  reauth: () => void;
  progressStep: () => void;
};


export default function ShopifyConnectionView({
  storeName,
  shop,
  reauth,
  progressStep,
}: ShopifyConnectionViewProps) {
  const { t } = useTranslation();
  const {currentUser, currentCollection} = useSelector((state: RootState) => state.user);
  const [storeDomain, setStoreDomain] = useState(storeName);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState("");
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isShopChanged, setIsShopChanged] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const formref = useRef<HTMLFormElement>(null);

  const handleConfirm = async () => {
    analytics.sendShopifyConnectionCompletedEvent({
      email: currentUser?.email ?? "unknown",
    })
    setIsConfirming(true);
    const collectionUpdateRes = await updateMiqCollection({
      id: currentCollection?.collection?.id!,
      miqImportMethod: "shopify",
      miqShopifyDomain: shop!,
      name: shop!.replace(".myshopify.com", ""),
    })
    if (!collectionUpdateRes) {
      alert("Error updating user");
      setIsConfirming(false);
    }
    setIsConfirming(false);
    progressStep();
  };

  const handleSupportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      formData.append("feedback", feedback);
      formData.append("type", "enterprise");

      const response = await fetch(
        "https://hooks.zapier.com/hooks/catch/15763147/28i1w52",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response.ok) {
        setShowSuccessAlert(true);
        setEmail("");
        setFeedback("");
        // Show success message and close dialog after delay
        setTimeout(() => {
          setShowSuccessAlert(false);
          setSupportDialogOpen(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting support request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (shop !== storeDomain) {
      setIsShopChanged(true);
    }
  }, [storeDomain, shop]);

  return (
    <form
      id="install-app-form"
      action={`${shopifyBackendBaseUrl}/authorize`}
      method="GET"
      ref={formref}
    >
      <div>
        <div className="space-y-6">
          {/* Hidden store inputs */}
          <input
            type="hidden"
            name="redirectPath"
            value={window.location.href}
          />
          <input
            type="hidden"
            id="shop"
            name="shop"
            value={storeDomain}
            onChange={(e) => setStoreDomain(e.target.value)}
          />

          {/* OAuth Section */}
          {!shop && (
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg space-y-4">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-blue-600" />
                <span className="font-medium">{t("shopify.oauth_authentication" as any as TranslationKey)}</span>
              </div>

              <Button
                className="w-full gap-2 bg-black hover:bg-gray-800 text-white relative overflow-hidden group"
                type="submit"
                onClick={async (e) => {
                  setConnecting(true);
                  analytics.sendShopifyConnectionStartedEvent({
                    email: currentUser?.email ?? "unknown",
                  })
                  await new Promise((resolve) => setTimeout(resolve, 2000));
                  formref.current?.submit();
                }}
                disabled={connecting}
              >
                <div className="absolute inset-0 w-0 bg-white/10 transition-all duration-[2s] ease-out group-active:w-full" />
                <span className="relative">{t("shopify.connect_shopify_store" as any as TranslationKey)}</span>
                {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                {!connecting && <ArrowRight className="h-4 w-4 relative" />}
              </Button>

              <div className="flex flex-col gap-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  <span>{t("shopify.credentials_encrypted" as any as TranslationKey)}</span>
                </div>
              </div>
            </div>
          )}
          {shop && (
            <div className="mt-4 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-md border border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  {t("shopify.store_name" as any as TranslationKey)}
                </label>
                <input
                  type="text"
                  value={storeDomain}
                  onChange={(e) => setStoreDomain(e.target.value)}
                  disabled
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 disabled:bg-gray-50 disabled:dark:bg-gray-800 disabled:text-gray-600 disabled:dark:text-gray-400 disabled:cursor-not-allowed focus:ring-1 focus:ring-black dark:focus:ring-gray-400"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t("shopify.your_store_url" as any as TranslationKey)}{" "}
                  {storeDomain.includes("myshopify.com")
                    ? storeDomain
                    : `${storeDomain}.myshopify.com`}
                </p>
              </div>

              {isShopChanged ? (
                <button
                  className="w-full flex items-center justify-center px-4 py-2 border rounded-md text-white font-medium transition-colors bg-black hover:bg-white hover:text-black hover:border-black"
                  type="submit"
                >
                  {t("shopify.change_shopify_store" as any as TranslationKey)}
                </button>
              ) : (
                <Button
                  disabled={isConfirming}
                  className="w-full"
                  variant="default"
                  onClick={handleConfirm}
                >
                  {isConfirming && (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  )}
                  {t("shopify.confirm_connection" as any as TranslationKey)}
                </Button>
              )}
            </div>
          )}

          {/* Help Resources */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t("shopify.need_help" as any as TranslationKey)}
            </h4>
            <div className="flex flex-col space-y-2">
              {/* Integration Guide Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 justify-start p-0"
                  >
                    <Info className="h-4 w-4 mr-2" />
                    {t("shopify.view_integration_guide" as any as TranslationKey)}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>{t("shopify.integration_guide_title" as any as TranslationKey)}</DialogTitle>
                    <DialogDescription>
                      {t("shopify.integration_guide_description" as any as TranslationKey)}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">{t("shopify.step1_title" as any as TranslationKey)}</h4>
                      <p className="text-sm text-gray-500">
                        {t("shopify.step1_description" as any as TranslationKey)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">{t("shopify.step2_title" as any as TranslationKey)}</h4>
                      <p className="text-sm text-gray-500">
                        {t("shopify.step2_description" as any as TranslationKey)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">{t("shopify.step3_title" as any as TranslationKey)}</h4>
                      <p className="text-sm text-gray-500">
                        {t("shopify.step3_description" as any as TranslationKey)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="font-medium">{t("shopify.step4_title" as any as TranslationKey)}</h4>
                      <p className="text-sm text-gray-500">
                        {t("shopify.step4_description" as any as TranslationKey)}
                      </p>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Support Dialog */}
              <Dialog
                open={supportDialogOpen}
                onOpenChange={setSupportDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 justify-start p-0"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {t("shopify.contact_support" as any as TranslationKey)}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>{t("shopify.contact_support_title" as any as TranslationKey)}</DialogTitle>
                    <DialogDescription>
                      {t("shopify.contact_support_description" as any as TranslationKey)}
                    </DialogDescription>
                  </DialogHeader>
                  <form
                    onSubmit={handleSupportSubmit}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                      >
                        {t("shopify.email" as any as TranslationKey)}
                      </label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("shopify.email_placeholder" as any as TranslationKey)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="feedback"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                      >
                        {t("shopify.message" as any as TranslationKey)}
                      </label>
                      <Textarea
                        id="feedback"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder={t("shopify.message_placeholder" as any as TranslationKey)}
                        required
                      />
                    </div>
                    {showSuccessAlert && (
                      <Alert className="bg-green-50 dark:bg-green-900">
                        <AlertDescription>
                          {t("shopify.message_sent_success" as any as TranslationKey)}
                        </AlertDescription>
                      </Alert>
                    )}
                    <DialogFooter>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting && (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        )}
                        {t("shopify.send_message" as any as TranslationKey)}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
